// Pay via UPI (§8): type a UPI ID or mobile (or, on mobile, scan a QR),
// verify the payee, enter the amount, review and authorise with the UPI PIN.
// No beneficiary is needed and none is created (§8.1).
import { useCallback, useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router';
import { useSelector } from 'react-redux';
import { v4 as uuidv4 } from 'uuid';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faArrowLeft, faCircleCheck } from '@fortawesome/free-solid-svg-icons';
import { checkLimits, executePayment, getPaymentContext } from '@/api/payments';
import { resolveUpiIdOrMobile } from '@/api/upi';
import { ApiError } from '@/api/client';
import type { RootState } from '@/store';
import type { PaymentContext, UpiResolution } from '@/types/phase2.types';
import { formatIndianCurrency } from '@/utils/currency';
import { RAIL_LABELS, UPI_DAILY_CAP_RUPEES, formatWholeRupees } from '@/utils/payments';
import { filterAmountInput, filterPayeeInput } from '@/utils/phase2Validation';
import { formatShort } from '@/mock/clock';
import Button from '@/components/ui/Button/Button';
import { usePaymentAuth } from '../components/usePaymentAuth';
import PaymentAuthSheets from '../components/PaymentAuthSheets';
import '../pay.css';

type Step = 'details' | 'review';

const productTypeOf = (accountType?: string): PaymentContext['productType'] => {
  const value = (accountType ?? '').toUpperCase();
  if (value.includes('CURRENT')) return 'CURRENT';
  if (value.includes('SALARY')) return 'SALARY';
  return 'SAVINGS';
};

const UpiPay = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const selectedAccount = useSelector((state: RootState) => state.account.selectedAccount);

  const [step, setStep] = useState<Step>('details');
  const [context, setContext] = useState<PaymentContext | null>(null);

  const [payeeInput, setPayeeInput] = useState('');
  const [verifying, setVerifying] = useState(false);
  const [payee, setPayee] = useState<UpiResolution | null>(null);
  const [verifyError, setVerifyError] = useState('');

  const payeeDestination = (p: UpiResolution): string => p.upiId ?? (p.mobile ? `+91 ${p.mobile}` : '');

  const [amountStr, setAmountStr] = useState('');
  const [remark, setRemark] = useState('');
  const [limitError, setLimitError] = useState('');
  const [continuing, setContinuing] = useState(false);
  const [idempotencyKey, setIdempotencyKey] = useState('');
  const [reviewError, setReviewError] = useState('');

  const amount = Number(amountStr);
  const amountValid = amountStr !== '' && Number.isFinite(amount) && amount > 0;
  const approvalLine = `Approve ${formatIndianCurrency(amount)} to ${payee?.name ?? ''} via UPI`;

  const loadContext = useCallback(async () => {
    try {
      setContext(
        await getPaymentContext({
          balanceRupees: selectedAccount?.availableBalance,
          productType: productTypeOf(selectedAccount?.accountType),
        })
      );
    } catch {
      setContext(null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const verify = useCallback(async (raw: string) => {
    const trimmed = raw.trim();
    if (!trimmed) return;
    setVerifying(true);
    setVerifyError('');
    setPayee(null);
    try {
      // Mobile number → resolved to its UPI handle (§8.2.1).
      const digitsOnly = /^[0-9]{10}$/.test(trimmed);
      const result = await resolveUpiIdOrMobile(
        digitsOnly ? { kind: 'MOBILE', mobile: trimmed } : { kind: 'UPI_ID', upiId: trimmed }
      );
      if (result.flagged) {
        // Flagged UPI accounts cannot receive payments (§8.2.4).
        setVerifyError("This UPI account can't receive payments right now.");
      } else {
        setPayee(result);
      }
    } catch (err) {
      setVerifyError(
        err instanceof ApiError && err.code !== 'SERVICE'
          ? err.message
          : "We couldn't verify this UPI ID or mobile."
      );
    } finally {
      setVerifying(false);
    }
  }, []);

  useEffect(() => {
    void loadContext();
    const to = searchParams.get('to');
    if (to) {
      setPayeeInput(to);
      void verify(to);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Mint one idempotency key per review open; retries reuse it (§7.3).
  useEffect(() => {
    if (step === 'review') setIdempotencyKey(uuidv4());
  }, [step]);

  const handleContinue = async () => {
    if (!payee || !amountValid) return;
    setLimitError('');
    setContinuing(true);
    try {
      const result = await checkLimits({
        rail: 'UPI',
        amountRupees: amount,
        chargesRupees: 0,
      });
      if (!result.ok) {
        setLimitError(result.message);
        return;
      }
      setStep('review');
    } catch (err) {
      setLimitError(err instanceof ApiError ? err.message : 'Could not check the limit. Try again.');
    } finally {
      setContinuing(false);
    }
  };

  const execute = async (authTicket: string, upiPin?: string) => {
    if (!payee) return;
    setReviewError('');
    try {
      const payment = await executePayment({
        idempotencyKey,
        rail: 'UPI',
        amountRupees: amount,
        payeeName: payee.name,
        destination: payee.upiId ?? payeeDestination(payee),
        remark: remark.trim() || undefined,
        debitAccountNumber: selectedAccount?.accountNumber,
        authorizationPin: upiPin,
        authTicket,
      });
      navigate('/pay/outcome', { state: { payment }, replace: true });
    } catch (err) {
      setReviewError(err instanceof ApiError ? err.message : 'Payment failed. Try again.');
    }
  };

  const auth = usePaymentAuth(execute);

  const startAuth = async () => {
    setReviewError('');
    const error = await auth.start('UPI', approvalLine, payee?.upiId);
    if (error) setReviewError(error);
  };

  return (
    <div className="nova-pay-flow">
      <div className="nova-pay-flow-header">
        <button
          type="button"
          className="nova-icon-btn"
          title="Back"
          aria-label="Back"
          onClick={() => (step === 'review' ? setStep('details') : navigate('/pay'))}
        >
          <FontAwesomeIcon icon={faArrowLeft} />
        </button>
        <h2>{step === 'details' ? 'Pay via UPI' : 'Review payment'}</h2>
      </div>

      {step === 'details' && (
        <>
          <label className="form-label">UPI ID or mobile number</label>
          <div className="nova-upi-hero">
            <input
              type="text"
              className="form-control"
              placeholder="UPI ID or mobile number"
              aria-label="UPI ID or mobile number"
              value={payeeInput}
              onChange={(e) => {
                setPayeeInput(filterPayeeInput(e.target.value));
                setPayee(null);
                setVerifyError('');
              }}
            />
            <Button onClick={() => void verify(payeeInput)} isLoading={verifying} disabled={!payeeInput.trim()}>
              Verify
            </Button>
          </div>

          {verifyError && <div className="nova-limit-box">{verifyError}</div>}

          {payee && (
            <div className="nova-upi-verified">
              <span className="nova-ben-avatar">{payee.name.charAt(0).toUpperCase()}</span>
              <span className="nova-ben-row-text">
                <span className="nova-ben-alias">{payee.name}</span>
                <span className="nova-ben-count">{payeeDestination(payee)}</span>
              </span>
              <span className="nova-upi-verified-icon">
                <FontAwesomeIcon icon={faCircleCheck} />
              </span>
            </div>
          )}

          <div className="mb-3 mt-3">
            <label className="form-label">Amount</label>
            <div className="input-group">
              <span className="input-group-text">₹</span>
              <input
                type="text"
                inputMode="decimal"
                className={`form-control ${amountStr !== '' && !amountValid ? 'is-invalid' : ''}`}
                value={amountStr}
                onChange={(e) => setAmountStr(filterAmountInput(e.target.value, amountStr))}
              />
            </div>
            {amountStr !== '' && !amountValid && (
              <div className="invalid-feedback d-block">Invalid Amount. Please enter valid amount.</div>
            )}
          </div>

          <p className="nova-pay-context-line">
            Up to {formatWholeRupees(UPI_DAILY_CAP_RUPEES)} a day via UPI
            {context && (
              <>
                {' '}
                · Today {formatWholeRupees(context.dailyUsedRupees)} of{' '}
                {formatWholeRupees(context.dailyLimitRupees)} used
              </>
            )}
          </p>

          <div className="mb-3">
            <label className="form-label">Remark (optional)</label>
            <input
              type="text"
              className="form-control"
              placeholder="What is this for?"
              value={remark}
              onChange={(e) => setRemark(e.target.value)}
            />
          </div>

          {limitError && <div className="nova-limit-box">{limitError}</div>}
          {context?.newDeviceUntil && (
            <p className="nova-pay-newdevice">
              New device: limited to ₹50,000 until {formatShort(context.newDeviceUntil)}.
            </p>
          )}

          <Button onClick={() => void handleContinue()} disabled={!payee || !amountValid} isLoading={continuing}>
            Continue
          </Button>
        </>
      )}

      {step === 'review' && payee && (
        <>
          <div className="nova-review-amount">
            <span className="nova-review-amount-value">{formatIndianCurrency(amount)}</span>
            <span className="nova-review-amount-to">to {payee.name}</span>
          </div>
          <dl className="nova-receipt-rows mb-4">
            <div className="nova-receipt-row"><dt>From</dt><dd>Your NovaBank account</dd></div>
            <div className="nova-receipt-row"><dt>To</dt><dd>{payee.name}</dd></div>
            <div className="nova-receipt-row"><dt>Destination</dt><dd>{payeeDestination(payee)}</dd></div>
            <div className="nova-receipt-row"><dt>Mode</dt><dd>{RAIL_LABELS.UPI}</dd></div>
            <div className="nova-receipt-row"><dt>Amount</dt><dd>{formatIndianCurrency(amount)}</dd></div>
            <div className="nova-receipt-row"><dt>Charges</dt><dd>{formatIndianCurrency(0)}</dd></div>
            {remark.trim() && <div className="nova-receipt-row"><dt>Remark</dt><dd>{remark.trim()}</dd></div>}
          </dl>

          <p className="nova-review-note">You'll confirm with your UPI PIN.</p>
          {reviewError && <div className="nova-limit-box">{reviewError}</div>}

          <div className="nova-form-footer">
            <Button variant="outline" onClick={() => setStep('details')}>
              Back
            </Button>
            <Button onClick={() => void startAuth()}>Pay with UPI PIN</Button>
          </div>
        </>
      )}

      <PaymentAuthSheets
        authState={auth.authState}
        approvalLine={approvalLine}
        pinError={auth.pinError}
        pinResetKey={auth.pinResetKey}
        pinLength={auth.pinLength}
        onPinLengthChange={auth.setPinLength}
        otpExpiry={auth.otpExpiry}
        otpSessionKey={auth.otpSessionKey}
        onCompletePin={(pin) => void auth.completePin(pin)}
        onVerifyOtp={auth.verifyOtp}
        onResendOtp={auth.resendOtp}
        onClose={auth.close}
      />
    </div>
  );
};

export default UpiPay;
